<template>
  <o-dialog
    ref="dialog"
    :title="$t('order.new')"
    :info="$t('components.order.actions.createOrder.info')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.confirm'), type: 'confirm', callback: confirm, disabled: !hasCustomer },
    ]"
    :portal="portal"
    :valid="validate"
  >
    <div class="pt-4 text-left">
      <o-form ref="form">
        <o-field-group
          v-slot="{ status }"
          :label="$t('general.customer')"
          :name="$t('general.customer')"
          class="mb-4 block"
          rules="required"
        >
          <customer-select v-model="customer" :status="status" create />
        </o-field-group>

        <o-field-group
          v-if="hasReservationItems"
          :label="$t('components.order.actions.createOrder.saleReportingDate')"
          :name="$t('components.order.actions.createOrder.saleReportingDate')"
          :info="$t('components.order.actions.createOrder.saleReportingDateInfo')"
          class="mb-4 block"
          rules="required"
        >
          <select-field
            v-model="time"
            :options="[
              { label: $t('components.order.actions.createOrder.appointmentDate'), value: 'reservation' },
              { label: $t('general.todaysDate'), value: 'now' }
            ]"
          />
        </o-field-group>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import { Reservation } from '@/_api/models/reservation'

import CustomerSelect from '@/components/customer/elements/Select.vue'
import { SelectField } from '@/components/fields'

interface Props {
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()

const { $api, $notification, $bus, $panel, $date } = useNuxtApp()
const { t } = useI18n()

const dialog = ref<any>()
const form = ref<any>()

const items = ref<any[]>([])
const customer = ref<any>(null)
const time = ref('now')

const hasReservationItems = computed(() => {
  return items.value?.some((item: any) => item instanceof Reservation)
})

const hasCustomer = computed(() => {
  const customerVal = customer.value

  if (customerVal) {
    if (customerVal.isNewRecord) {
      return !!customerVal.firstName.length && !!customerVal.lastName.length
    } else {
      return true
    }
  }

  return false
})

function open ({ items: openItems = [], customer: openCustomer, date = new Date() }: { items?: any[], customer?: any, date?: Date }) {
  items.value = openItems
  customer.value = openCustomer

  time.value = hasReservationItems.value ? 'reservation' : 'now'

  dialog.value.open()
}

async function confirm () {
  emit('update:loading', true)

  try {
    if (customer.value.isNewRecord) {
      const savedCustomer = await saveCustomer()

      if (!savedCustomer) {
        emit('update:loading', false)

        return
      }
    }

    const itemsVal = items.value
    const customerVal = customer.value
    const timeVal = time.value

    const today = new Date()
    let date = ($date as any).timestamp(today)

    if (timeVal === 'reservation' && itemsVal) {
      const reservation = itemsVal.find((item: any) => item instanceof Reservation)
      date = ($date as any).timestamp(reservation.date.date)
    }

    const mappedItems = itemsVal.map((item: any) => ({
      type: item.constructor?.modelName?.() || '',
      id: item.objectID?.() || '',
    }))

    const customerId = customerVal.idCustomer?.toString() || customerVal.objectID?.()
    const response = await $api.order.create({
      customerId,
      notes: '',
      items: mappedItems,
      date,
    })

    const order = response?.data

    if (order) {
      emit('success')

      for (const item of itemsVal) {
        const modelName = item?.constructor?.modelName?.()
        if (modelName) {
          const name = modelName.charAt(0).toUpperCase() + modelName.slice(1)
          $bus.emit.updated(name, item)
        }
      }

      $notification.toast({
        title: t('components.order.actions.createOrder.success'),
        text: t('components.order.actions.createOrder.successInfo'),
        variant: 'success',
      })

      $panel.create(`/order/${order.idOrder}`)

      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  emit('update:loading', false)
}

async function saveCustomer () {
  try {
    const response = await $api.customer.save(customer.value)
    const savedCustomer = response.data

    if (savedCustomer) {
      customer.value = savedCustomer

      $notification.toast({
        title: t('customer.createdSuccess'),
        text: t('customer.createdSuccessInfo', { name: savedCustomer.displayName }),
        variant: 'success',
      })

      $bus.emit.created('Customer', savedCustomer)

      return true
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
  }
}

function validate () {
  return form.value?.validate()
}

defineExpose({ open })
</script>
